import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, MagnifyingGlass, CheckCircle, ArrowRight, Trash } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { getCategoryName, getSubcategoryName } from '../utils/categoryNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setData(d);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchNotifications();
  }, [user, navigate, fetchNotifications]);

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setData(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => ({ ...n, read: true })),
        unread_count: 0,
      }));
      toast.success(t('notifications.allRead'));
    } catch {
      toast.error('Failed');
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.read) {
      try {
        await axios.put(`${API}/notifications/${n.notification_id}/read`, {}, { withCredentials: true });
        setData(prev => ({
          ...prev,
          notifications: prev.notifications.map(x => x.notification_id === n.notification_id ? { ...x, read: true } : x),
          unread_count: Math.max(0, prev.unread_count - 1),
        }));
      } catch (error) { console.error('Mark read failed:', error); }
    }
    const params = new URLSearchParams();
    if (n.category) params.set('category', n.category);
    if (n.subcategory) params.set('subcategory', n.subcategory);
    params.set('sort', 'newest');
    navigate(`/browse?${params.toString()}`);
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === data.notifications.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.notifications.map(n => n.notification_id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const ids = Array.from(selected);
      await axios.post(`${API}/notifications/delete`, { notification_ids: ids }, { withCredentials: true });
      const deletedUnread = data.notifications.filter(n => ids.includes(n.notification_id) && !n.read).length;
      setData(prev => ({
        ...prev,
        notifications: prev.notifications.filter(n => !ids.includes(n.notification_id)),
        unread_count: Math.max(0, prev.unread_count - deletedUnread),
      }));
      setSelected(new Set());
      toast.success(t('notifications.deleted', { count: ids.length }));
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const getNotifLabel = (n) => {
    const sub = n.subcategory || '';
    const cat = n.category || '';
    if (sub && cat) return `${getCategoryName(cat, t)} > ${getSubcategoryName(sub, t)}`;
    if (cat) return getCategoryName(cat, t);
    return n.search_name || n.label || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  const allSelected = data.notifications.length > 0 && selected.size === data.notifications.length;

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <Header />
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide" data-testid="notifications-title">
            {t('notifications.title')}
          </h1>
          <div className="flex items-center gap-2">
            {data.unread_count > 0 && (
              <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="text-xs gap-1.5" data-testid="mark-all-read-btn">
                <CheckCircle size={14} />
                {t('notifications.markAllRead')}
              </Button>
            )}
          </div>
        </div>

        {data.notifications.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="no-notifications">
            <Bell size={48} className="text-text_muted mx-auto mb-3" />
            <p className="text-text_muted">{t('notifications.empty')}</p>
          </div>
        ) : (
          <>
            {/* Selection toolbar */}
            <div className="flex items-center justify-between mb-3 px-1">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="select-all-notifications">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  className="border-border"
                />
                <span className="text-xs text-text_muted">{t('notifications.selectAll')}</span>
              </label>
              {selected.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                  className="text-xs gap-1.5 text-danger border-danger hover:bg-danger-soft"
                  data-testid="delete-selected-btn"
                >
                  <Trash size={14} />
                  {deleting ? '...' : t('notifications.deleteSelected', { count: selected.size })}
                </Button>
              )}
            </div>

            <div className="space-y-2" data-testid="notifications-list">
              {data.notifications.map((n) => (
                <div
                  key={n.notification_id}
                  className={`bg-white border rounded-xl p-4 flex items-center gap-3 transition-all hover:shadow-sm hover:border-primary/30 group ${
                    n.read ? 'border-border' : 'border-primary/30 bg-primary/[0.02]'
                  } ${selected.has(n.notification_id) ? 'ring-2 ring-primary/20' : ''}`}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(n.notification_id)}
                      onCheckedChange={() => toggleSelect(n.notification_id, { stopPropagation: () => {} })}
                      className="border-border"
                      data-testid={`select-notification-${n.notification_id}`}
                    />
                  </div>
                  <button
                    onClick={() => handleNotificationClick(n)}
                    className="flex-1 flex items-center gap-3 text-left min-w-0"
                    data-testid={`notification-click-${n.notification_id}`}
                  >
                    <div className={`p-2.5 rounded-full flex-shrink-0 ${n.read ? 'bg-surface_muted' : 'bg-primary/10'}`}>
                      <MagnifyingGlass size={18} className={n.read ? 'text-text_muted' : 'text-primary'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${n.read ? 'text-text_muted' : 'text-text_main font-semibold'}`} data-testid="notification-text">
                        {n.match_count === 1
                          ? t('notifications.newListingSingle', { label: getNotifLabel(n) })
                          : t('notifications.newListingPlural', { count: n.match_count, label: getNotifLabel(n) })
                        }
                      </p>
                      <p className="text-xs text-text_muted mt-1">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-text_muted group-hover:text-primary transition-colors flex-shrink-0" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};
